/**
 * F-506-FE-02: LoginPage
 *
 * Hospital-specific doctor login at /login/:hospitalSlug.
 * - Displays hospital name fetched from GET /api/register/:hospitalSlug/info
 * - Fields: email, password
 * - On success: stores JWT via AuthContext.loginDoctor(), redirects to /dashboard
 * - "Forgot password?" link navigates to /forgot-password
 * - ?reset=success query param shows a one-time success banner (auto-dismissed on first input focus)
 * - Invalid slug shows "Registration unavailable." error page
 *
 * Design: Clean, institutional, trust-building. Clinical palette.
 * Mobile-first. Touch targets 44px minimum.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HospitalInfo {
  name: string;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

async function fetchHospitalInfo(slug: string): Promise<HospitalInfo> {
  const res = await fetch(`${BASE}/api/register/${slug}/info`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<HospitalInfo>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function LoginPage() {
  const { hospitalSlug } = useParams<{ hospitalSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loginDoctor, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Show success banner if arriving from registration
  const registrationSuccess =
    (location.state as { registrationSuccess?: boolean } | null)?.registrationSuccess === true;

  // Show one-time banner when arriving from a successful password reset
  const justReset = searchParams.get('reset') === 'success';

  function dismissResetBanner() {
    if (justReset) {
      setSearchParams({});
    }
  }

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // ---------------------------------------------------------------------------
  // Fetch hospital info
  // ---------------------------------------------------------------------------
  const {
    data: hospitalInfo,
    isLoading: isLoadingHospital,
    isError: isHospitalError,
  } = useQuery<HospitalInfo>({
    queryKey: ['hospital-info', hospitalSlug],
    queryFn: () => fetchHospitalInfo(hospitalSlug ?? ''),
    enabled: Boolean(hospitalSlug),
    retry: false,
  });

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !hospitalSlug) return;
    setLoginError(null);
    setIsSubmitting(true);
    try {
      const mfaResult = await loginDoctor(hospitalSlug, email.trim(), password);
      // P2.5: MFA required — redirect to verify page with mfa-only token
      if (mfaResult?.mfaRequired) {
        navigate('/mfa/verify', { state: { mfaToken: mfaResult.mfaToken, hospitalSlug } });
        return;
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 401) {
        setLoginError('Incorrect email or password. Please try again.');
      } else if (e.status === 403) {
        setLoginError(
          'Your account is not yet active. Please contact your hospital administrator.',
        );
      } else if (e.status === 429) {
        setLoginError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setLoginError(e.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (isLoadingHospital) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-clinical-amber border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-body text-clinical-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Invalid / inactive slug — no details (prevent enumeration)
  // ---------------------------------------------------------------------------
  if (isHospitalError || (hospitalInfo && !hospitalInfo.isActive)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
        <div className="clinical-card p-8 text-center max-w-sm w-full">
          <div className="w-12 h-12 rounded-full bg-clinical-cream-dark flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-clinical-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <p className="font-body text-clinical-charcoal font-medium">
            Registration unavailable.
          </p>
        </div>
      </div>
    );
  }

  const hospitalName = hospitalInfo?.name ?? hospitalSlug ?? '';

  // ---------------------------------------------------------------------------
  // Login form
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Hospital header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-clinical-charcoal flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
              />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal leading-tight">
            {hospitalName}
          </h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Sign in to your account
          </p>
        </div>

        {/* Registration success banner */}
        {registrationSuccess && (
          <div
            role="status"
            className="mb-4 p-3 rounded-clinical bg-green-50 border border-green-200 text-sm font-body text-green-700 flex items-center gap-2"
          >
            <svg
              className="w-4 h-4 flex-shrink-0 text-safety-green"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
              />
            </svg>
            Account created. Your administrator will activate it shortly.
          </div>
        )}

        {/* Password reset success banner — auto-dismissed on first input focus */}
        {justReset && (
          <div
            role="status"
            className="rounded-clinical border border-teal-200 bg-teal-50 px-4 py-3 mb-4"
          >
            <p className="text-sm font-body text-teal-800">
              Password updated. Sign in with your new password.
            </p>
          </div>
        )}

        {/* Form card */}
        <div className="clinical-card p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="login-email" className="form-label">
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={dismissResetBanner}
                className="form-input"
                placeholder="you@hospital.com"
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="login-password" className="form-label mb-0">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-body text-clinical-teal hover:text-clinical-teal-dark transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="Your password"
              />
            </div>

            {/* Login error */}
            {loginError && (
              <div
                role="alert"
                className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
              >
                {loginError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={
                isSubmitting ||
                email.trim().length === 0 ||
                password.length === 0
              }
              className="btn-primary w-full"
            >
              {isSubmitting ? (
                <>
                  <span
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Register link */}
        {hospitalSlug && (
          <p className="text-center text-sm font-body text-clinical-text-muted mt-6">
            Need an account?{' '}
            <Link
              to={`/register/${hospitalSlug}`}
              className="text-clinical-teal hover:text-clinical-teal-dark underline-offset-2 hover:underline transition-colors"
            >
              Register here
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
